import { useCallback, useEffect, useState } from 'react';
import getCMResponse from '../../../utils/api_manager';
import { saveForShopifyRedirect } from '../../../utils/shopifyGrantFlowProject';

// Shopify data-access trigger for the unified Grant Access grid
// (GrantOnboardingAccess.jsx / PlatformTile), following the same
// onLoginReady contract as the Google*Auth components: it registers a
// `login()` function the tile's onClick calls directly (preserving the user
// gesture, same reasoning as the Google popup flows even though Shopify uses
// a plain redirect rather than a popup).
//
// This reuses the request-permissions call that used to live in the
// standalone ShopifyAuth.jsx component (rendered during project
// creation) — same backend contract (GET /datasource/shopify/request-permissions
// -> { redirect_url }, then a full-page redirect to Shopify's OAuth consent
// screen). Two things changed from that old component:
//   1. The endpoint path — the old component called `/auth/shopify/...`,
//      which was never actually mounted (see cq-api's shopifyRouter, mounted
//      at /datasource/shopify). Fixed to /datasource/shopify/request-permissions.
//   2. The shop domain no longer needs to be entered here — it was already
//      captured at project-creation time (EnterOrganizationDetails.jsx)
//      and is available as project.shopify_store_url (per
//      project.service.js's createProject response shape).
//
// Shopify's OAuth callback (cq-api's shopifyRouter '/callback') redirects the
// full browser back to /grant-access, so this component's tile naturally
// re-fetches data sources on remount — no extra client-side "connected" flag
// is needed here beyond the fetchDataSources() that GrantOnboardingAccess
// already runs on mount.
const ShopifyOnboardingAuth = ({ project, onLoginReady }) => {
  const [error, setError] = useState(null);

  const login = useCallback(() => {
    setError(null);
    const shopDomain = project?.shopify_store_url;

    if (!shopDomain) {
      setError('No Shopify store is configured for this project.');
      return;
    }

    getCMResponse('/datasource/shopify/request-permissions', 'GET', {
      shop_domain: shopDomain,
      project_id: project.id,
    })
      .then((response) => {
        // connect-cta-new-tab-project-leak-trd.md Section 2.3/2.4,
        // Section 3 R7: this is the one remaining case that still needs a
        // storage-backed signal -- the same-tab full-page Shopify OAuth
        // redirect below, whose return leg cannot always carry a
        // project ID in its path (cq-api's cookie-missing callback
        // branch). Saved immediately before the redirect away, consumed
        // (read once, then unconditionally cleared) on return by
        // LegacyProjectRedirect's /grant-access alias.
        saveForShopifyRedirect(project);
        window.location.href = response.redirect_url;
      })
      .catch((err) => {
        console.error('Error initiating Shopify OAuth:', err);
        setError('Failed to initiate Shopify authorization. Please try again.');
      });
  }, [project]);

  // Expose login to parent so the tile's click handler can call it directly.
  useEffect(() => {
    onLoginReady?.(login);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // In managed mode (always true here — PlatformTile is the trigger) render
  // nothing but a surfaced error, matching GoogleMerchantCenterAuth's
  // managed-mode convention.
  if (onLoginReady) {
    return error ? (
      <p role="alert" style={{ color: 'var(--color-error)', fontSize: 12 }}>
        {error}
      </p>
    ) : null;
  }

  return null;
};

export default ShopifyOnboardingAuth;
